import type { ConfigurationKey, EXPERIMENT_KEYS, ExperimentKey, ExperimentVariantsOf, FeatureKey } from '#shared/constants/experiment'

export default function useExperiment() {
  const experiments = useExperimentsConfig()
  const { isWebView } = usePlatform()

  /**
   * Gets an experiment from the ConfigCat config.
   *
   * @param experimentKey The key of the experiment.
   * @returns The value of the experiment.
   */
  function getExperiment(experimentKey: EXPERIMENT_KEYS) {
    if (!experiments.value) {
      return null
    }

    return experiments.value[experimentKey]
  }

  /**
   * Gets all the variants for all experiments.
   *
   * @returns An object with experiment keys and variant values.
   */
  function getAllVariants() {
    if (!experiments.value) {
      return {}
    }

    return Object.entries(experiments.value).reduce((acc, [experimentKey, experiment]) => {
      acc[experimentKey] = experiment?.variant ?? ''
      return acc
    }, {} as Record<string, boolean | number | string>)
  }

  /**
   * Gets the assigned variant for a multivariate experiment.
   *
   * @param experimentKey The key of the experiment.
   * @returns The assigned variant, or undefined when unavailable.
   */
  function getExperimentVariant<K extends ExperimentKey>(experimentKey: K): ExperimentVariantsOf<K> | undefined {
    const experiment = getExperiment(experimentKey)

    if (!experiment || experiment.type !== 'Experiment') {
      return undefined
    }

    return (experiment.variant ?? undefined) as ExperimentVariantsOf<K> | undefined
  }

  /**
   * Reads the value of a configuration flag, such as a sample rate.
   *
   * @param experimentKey The key of the configuration flag.
   * @returns The configured value, or undefined when unavailable.
   */
  function getConfigurationValue<K extends ConfigurationKey>(experimentKey: K): ExperimentVariantsOf<K> | undefined {
    const experiment = getExperiment(experimentKey)

    if (!experiment || experiment.type !== 'Configuration') {
      return undefined
    }

    return (experiment.variant ?? undefined) as ExperimentVariantsOf<K> | undefined
  }

  /**
   * Check if a feature is on or off.
   *
   * @param experimentKey The key of the feature flag.
   * @returns true when the feature is on.
   */
  function isFeatureEnabled(experimentKey: FeatureKey) {
    const experiment = getExperiment(experimentKey)

    if (!experiment || experiment.type !== 'Feature') {
      return false
    }

    return Boolean(experiment.variant)
  }

  /**
   * Tracks a view for the experiment.
   *
   * @param experimentKey The key of the experiment.
   * @returns undefined | void
   */
  function trackView(experimentKey: EXPERIMENT_KEYS) {
    const experiment = getExperiment(experimentKey)

    if (!experiment) {
      return
    }

    if (
      experiment.tracked
      && (!isWebView.value || experiment.trackWebview)
      && (experiment.variant !== experiment.defaultVariant || experiment.trackDefaultVariant)
    ) {
      trackExperimentViewed({
        experimentName: experiment.key,
        variantName: `${experiment.variant}`,
      })
    }
  }

  // The "Canadian Owner" banner is shown to Canadian guests only, when the
  // feature flag is enabled.
  const { isInCa, isInUs } = useGeolocation()
  const showCanadianOwnerBanner = computed(() => isFeatureEnabled('rvz22206CanadianOwnedCampaign') && isInCa.value)

  // Wether or not to show the Download App modal in the search page.
  const showDownloadAppModalInSearch = computed(() => isFeatureEnabled('rvz24790ShowDownloadAppModalInSearch'))

  // Whether to show the motorhome discounts banner.
  const showMotorhomeDiscountBanner = computed(() => isFeatureEnabled('rvz26829MotorhomeDiscountsBanner'))

  // Whether to show the Thanksgiving banner (US guests only; 'off' is not shown).
  const showThanksGivingBanner = computed(() => getExperimentVariant('rvz20599ThanksgivingBanner') === 'US' && isInUs.value)

  // Wether to show the Travel trailer promotional banner.
  const showTravelTrailerDiscountBanner = computed(() => isFeatureEnabled('rvz27027TravelTrailersPromotion'))

  return {
    getExperiment,
    getAllVariants,
    getConfigurationValue,
    getExperimentVariant,
    isFeatureEnabled,
    trackView,

    showCanadianOwnerBanner,
    showDownloadAppModalInSearch,
    showMotorhomeDiscountBanner,
    showThanksGivingBanner,
    showTravelTrailerDiscountBanner,
  }
}
